// src/components/pages/Services.tsx
import { useState, useCallback, Fragment } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Pencil,
  Plus,
  ChevronDown,
  GripVertical,
  FolderOpen,
  Trash2,
  Wrench,
  SearchX,
  X,
  AlertTriangle,
} from 'lucide-react';
import { lucideIcons } from '@/lib/lucideIcons';

// Minutos que suman las fases menos la duracion del servicio. Positivo = las
// fases se pasan y la cita ocupara mas de lo que dice el servicio.
const desvioFases = (s: Servicio) => {
  const fases = s.fases_servicio ?? [];
  if (fases.length === 0) return 0;
  const total = fases.reduce(
    (acc, f) => acc + (Number(f.duracion_minutos) || 0),
    0
  );
  return total - (Number(s.duracion_minutos) || 0);
};
import Joyride, { Step } from 'react-joyride';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import {
  useServiciosPorEmpresa,
  Servicio,
} from '@/components/hooks/useServicios';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { EditServiceDialog } from '@/components/pages/dialogs/EditServiceDialog';
import { EditPhasesServiceDialog } from '@/components/pages/dialogs/EditPhasesServiceDialog';
import { ServiceDetailsDialog } from '@/components/pages/dialogs/ServiceDetailsDialog';
import {
  EditCategoryDialog,
  CategoriaServicio,
} from '@/components/pages/dialogs/EditCategoryDialog';
import getServicesTourSteps from '@/components/tour/company/ServicesTourSteps';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';
import { getTourStyles } from '@/components/tour/tourStyles';
import Pagination from '@/components/pages/shared/Pagination';

function LucideIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (lucideIcons as any)[name];
  if (!Icon) return null;
  return <Icon className={className ?? 'w-4 h-4'} />;
}

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

type SortKey =
  | 'servicio'
  | 'descripcion'
  | 'precio'
  | 'duracion'
  | 'publico'
  | 'fases'
  | 'activo';

type Grupo = {
  categoria: string;
  categoriaId: string | null;
  categoriaImagen?: string | null;
  servicios: Servicio[];
};

function agruparPorCategoria(servicios: Servicio[]): Grupo[] {
  const map = new Map<string, Grupo>();
  for (const s of servicios) {
    const id = s.id_categoria_servicio ?? null;
    const nombre: string = s.categoria_servicio?.nombre ?? '';
    const key = id ?? '__sin_categoria__';
    if (!map.has(key)) {
      const imagen = (s as any).categoria_servicio?.imagen ?? null;
      map.set(key, {
        categoria: nombre,
        categoriaId: id,
        categoriaImagen: imagen,
        servicios: [],
      });
    }
    map.get(key)!.servicios.push(s);
  }
  const grupos = Array.from(map.values());
  grupos.sort((a, b) => {
    if (!a.categoriaId && b.categoriaId) return 1;
    if (a.categoriaId && !b.categoriaId) return -1;
    return a.categoria.localeCompare(b.categoria);
  });
  return grupos;
}

function DraggableRow({
  servicio,
  idx,
  isDragging,
  children,
}: {
  servicio: Servicio;
  idx: number;
  isDragging: boolean;
  children: (dragHandleProps: {
    dragListeners: any;
    dragAttributes: any;
  }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: servicio.id_servicio,
    data: { servicio },
  });
  const style = transform
    ? { transform: CSS.Translate.toString(transform), zIndex: 50 }
    : undefined;
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`transition-colors ${
        isDragging
          ? 'opacity-40 bg-blue-50 dark:bg-blue-950/30'
          : idx % 2 === 0
          ? 'bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-white/5'
          : 'bg-gray-50 dark:bg-white/[0.03] hover:bg-gray-100 dark:hover:bg-white/[0.06]'
      }`}
    >
      {children({ dragListeners: listeners, dragAttributes: attributes })}
    </tr>
  );
}

function DroppableGroupHeader({
  grupo,
  collapsed,
  onToggle,
  isOver,
  onEditCategory,
}: {
  grupo: Grupo;
  collapsed: boolean;
  onToggle: () => void;
  isOver: boolean;
  onEditCategory: (grupo: Grupo) => void;
}) {
  const key = grupo.categoriaId ?? '__sin_categoria__';
  const { setNodeRef } = useDroppable({ id: `drop-${key}` });
  const label = grupo.categoria || 'Sin categoría';
  return (
    <tr
      ref={setNodeRef}
      className={`transition-colors duration-150 tour-category-header tour-group-header ${
        isOver
          ? 'bg-blue-100 dark:bg-blue-900/40 ring-2 ring-inset ring-blue-400 dark:ring-blue-500'
          : 'bg-[#103254]/[0.06] dark:bg-blue-900/20'
      }`}
    >
      <td colSpan={10} className="px-4 py-2">
        <div className="flex items-center gap-2">
          {grupo.categoriaImagen && (
            <img
              src={grupo.categoriaImagen}
              alt={label}
              className="w-10 h-10 rounded-full object-cover border border-gray-300 dark:border-white/10"
            />
          )}
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#103254] dark:text-blue-300 hover:opacity-80 transition-opacity"
          >
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                collapsed ? '-rotate-90' : ''
              }`}
            />
            {label}
            <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal normal-case tracking-normal">
              ({grupo.servicios.length})
            </span>
          </button>
          {grupo.categoriaId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditCategory(grupo);
              }}
              className="ml-1 text-gray-400 hover:text-[#103254] dark:hover:text-blue-400 transition-colors"
              title={`Editar categoría "${label}"`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {isOver && (
            <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-medium animate-pulse">
              Soltar aquí →
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function DragOverlayCard({ servicio }: { servicio: Servicio }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-[#1c1c1e] border border-blue-400 dark:border-blue-500 rounded-lg shadow-2xl text-sm font-medium text-gray-800 dark:text-white cursor-grabbing w-80">
      <GripVertical className="w-4 h-4 text-blue-400 shrink-0" />
      {servicio.icono && (
        <LucideIcon
          name={servicio.icono}
          className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0"
        />
      )}
      <span className="truncate">{servicio.servicio}</span>
      {servicio.precio != null && (
        <span className="ml-auto text-gray-500 dark:text-gray-400 shrink-0">
          {servicio.precio.toFixed(2)} €
        </span>
      )}
    </div>
  );
}

export default function Services() {
  const { t } = useTranslation();
  const empresaId = localStorage.getItem('id_empresa') || '';
  const idUsuario = localStorage.getItem('id_usuario') || '';
  const isMobile = window.innerWidth < 640;

  const {
    servicios,
    loading,
    refetch,
    moverServicioCategoria,
    eliminarServicio,
  } = useServiciosPorEmpresa(empresaId, false);

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [runTour, setRunTour] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('servicio');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState<Servicio | null>(null);
  const [editingPhasesFor, setEditingPhasesFor] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<Servicio | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<CategoriaServicio | null>(null);
  const [activeServicio, setActiveServicio] = useState<Servicio | null>(null);
  const [overGroupKey, setOverGroupKey] = useState<string | null>(null);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla ni
  // categorías que enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  // ── Confirm delete ─────────────────────────────────────────────────────────
  const [confirmDeleteServicio, setConfirmDeleteServicio] =
    useState<Servicio | null>(null);

  const steps: Step[] = getServicesTourSteps(isMobile);
  const LS_PAGE_SIZE_KEY = 'services_page_size';
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === 'undefined') return 10;
    const saved = localStorage.getItem(LS_PAGE_SIZE_KEY);
    return saved ? Number(saved) : 10;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleEditCategory = useCallback((grupo: Grupo) => {
    if (!grupo.categoriaId) return;
    setEditingCategory({
      id_categoria_servicio: grupo.categoriaId,
      nombre: grupo.categoria,
      imagen: grupo.categoriaImagen ?? null,
    });
    setShowCategoryDialog(true);
  }, []);

  // ── Eliminar servicio ──────────────────────────────────────────────────────
  const handleEliminarServicio = useCallback(async () => {
    if (!confirmDeleteServicio) return;
    const { ok } = await eliminarServicio(confirmDeleteServicio.id_servicio);
    if (ok) {
      setOpenServiceDialog(false);
      setConfirmDeleteServicio(null);
      await refetch();
      window.dispatchEvent(new CustomEvent('servicios-actualizados'));
    }
  }, [confirmDeleteServicio, eliminarServicio, refetch]);

  // ── Filtrado + ordenación ──────────────────────────────────────────────────
  const filtered = servicios.filter((s) => {
    const q = filter.toLowerCase();
    return (
      s.servicio.toLowerCase().includes(q) ||
      (s.descripcion || '').toLowerCase().includes(q) ||
      (s.precio?.toString() || '').includes(q) ||
      (s.duracion_minutos?.toString() || '').includes(q) ||
      (s.servicio_publico ? 'sí' : 'no').includes(q) ||
      ((s.fases_servicio?.length ?? 0) > 0 ? 'sí' : 'no').includes(q)
    );
  });

  const formatDuration = (minutes?: number) => {
    if (minutes == null) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const parts: string[] = [];
    if (hrs) parts.push(`${hrs}h`);
    if (mins) parts.push(`${mins}m`);
    return parts.join(' ') || '-';
  };

  const sorted = [...filtered].sort((a, b) => {
    let aVal: any, bVal: any;
    switch (sortKey) {
      case 'servicio': {
        const getNum = (txt: string) => {
          const match = txt.match(/^\s*(\d+)\s*[.)-]?\s*/);
          return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
        };
        aVal = getNum(a.servicio);
        bVal = getNum(b.servicio);
        if (aVal === bVal) {
          aVal = a.servicio;
          bVal = b.servicio;
        }
        break;
      }
      case 'descripcion':
        aVal = a.descripcion || '';
        bVal = b.descripcion || '';
        break;
      case 'precio':
        aVal = a.precio ?? 0;
        bVal = b.precio ?? 0;
        break;
      case 'duracion':
        aVal = a.duracion_minutos ?? 0;
        bVal = b.duracion_minutos ?? 0;
        break;
      case 'publico':
        aVal = a.servicio_publico ? 1 : 0;
        bVal = b.servicio_publico ? 1 : 0;
        break;
      case 'fases':
        aVal = a.fases_servicio?.length ?? 0;
        bVal = b.fases_servicio?.length ?? 0;
        break;
      case 'activo':
        aVal = a.activo ? 1 : 0;
        bVal = b.activo ? 1 : 0;
        break;
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const grupos = agruparPorCategoria(sorted);
  const visibles = grupos.flatMap((g) => {
    const key = g.categoriaId ?? '__sin_categoria__';
    return collapsedGroups.has(key) ? [] : g.servicios;
  });
  const totalPages = Math.ceil(visibles.length / pageSize) || 1;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const visiblesPaginados = visibles.slice(startIndex, endIndex);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setPage(1);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
    localStorage.setItem(LS_PAGE_SIZE_KEY, String(size));
  };

  const renderSortIcon = (key: SortKey) =>
    sortKey === key ? (sortOrder === 'asc' ? '▲' : '▼') : null;

  const openCreateModal = () => {
    setEditingService(null);
    setOpenServiceDialog(true);
  };
  const openEditModal = (s: Servicio) => {
    setEditingService(s);
    setOpenServiceDialog(true);
  };

  // ── Drag handlers ──────────────────────────────────────────────────────────
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveServicio(event.active.data.current?.servicio ?? null);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id as string | undefined;
    if (overId?.startsWith('drop-'))
      setOverGroupKey(overId.replace('drop-', ''));
    else setOverGroupKey(null);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const draggedServicio = activeServicio;
      setActiveServicio(null);
      setOverGroupKey(null);

      const { over } = event;
      if (!over || !draggedServicio) return;

      const overId = over.id as string;
      if (!overId.startsWith('drop-')) return;

      const targetKey = overId.replace('drop-', '');
      const currentKey =
        draggedServicio.id_categoria_servicio ?? '__sin_categoria__';

      if (currentKey === targetKey) return;

      const newCategoriaId =
        targetKey === '__sin_categoria__' ? null : targetKey;

      const { ok } = await moverServicioCategoria(
        draggedServicio.id_servicio,
        newCategoriaId,
        idUsuario
      );

      if (!ok) {
        toast.error('Error al mover el servicio de categoría.');
        return;
      }

      const targetGrupo = grupos.find(
        (g) => (g.categoriaId ?? '__sin_categoria__') === targetKey
      );

      toast.success(
        `"${draggedServicio.servicio}" movido a "${
          targetGrupo?.categoria || 'Sin categoría'
        }".`
      );

      await refetch();
    },
    [activeServicio, grupos, idUsuario, moverServicioCategoria, refetch]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full space-y-6 pb-28 lg:pb-0"
      >
        {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
            y global (HelpButton), lanzado desde usePageHelp. */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 tour-title">
            <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {t('services.title')}
            </span>
          </div>
        </motion.div>

        {/* Joyride */}
        <motion.div variants={itemVariants}>
          <Joyride
            steps={steps}
            run={runTour}
            continuous
            showSkipButton
            spotlightClicks
            locale={{
              back: t('services.tour.back'),
              close: t('services.tour.close'),
              last: t('services.tour.last'),
              next: t('services.tour.next'),
              skip: t('services.tour.skip'),
            }}
            callback={({ status }) => {
              if (status === 'finished' || status === 'skipped')
                setRunTour(false);
            }}
            styles={getTourStyles(isMobile)}
          />
        </motion.div>

        {/* Filter + botones */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-3 justify-between items-center"
        >
          <Input
            type="text"
            placeholder={t('services.searchPlaceholder')}
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
          />
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={() => {
                setEditingCategory(null);
                setShowCategoryDialog(true);
              }}
              variant="outline"
              className="rounded-lg font-medium px-4 py-2 border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors w-full sm:w-auto tour-categories-btn"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Categorías
            </Button>
            {servicios.length > 0 && (
              <Button
                onClick={openCreateModal}
                className="bg-[#103254] hover:bg-[#0d2742] rounded-lg text-white font-medium shadow-md px-6 py-2 tour-add-btn w-full sm:w-auto transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('services.addBtn')}
              </Button>
            )}
          </div>
        </motion.div>

        {grupos.length > 1 && !loading && servicios.length > 0 && (
          <motion.p
            variants={itemVariants}
            className="hidden lg:flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 select-none tour-drag-hint"
          >
            <GripVertical className="w-3.5 h-3.5 shrink-0" />
            Arrastra el icono de una fila para mover el servicio a otra
            categoría
          </motion.p>
        )}

        <div className="tour-dialog" style={{ display: 'none' }} />

        {/* ── Dialogs ── */}
        <EditServiceDialog
          open={openServiceDialog && !editingPhasesFor}
          onClose={() => setOpenServiceDialog(false)}
          editingService={editingService}
          empresaId={empresaId}
          onSaved={() => {
            refetch();
            setOpenServiceDialog(false);
            window.dispatchEvent(new CustomEvent('servicios-actualizados'));
          }}
          onEditPhases={(id) => setEditingPhasesFor(id)}
        />

        {editingPhasesFor && (
          <EditPhasesServiceDialog
            serviceId={editingPhasesFor}
            open
            onClose={() => setEditingPhasesFor(null)}
          />
        )}

        <ServiceDetailsDialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          servicio={selectedService}
        />

        <EditCategoryDialog
          open={showCategoryDialog}
          onClose={() => {
            setShowCategoryDialog(false);
            setEditingCategory(null);
          }}
          empresaId={empresaId}
          mode={editingCategory ? 'edit' : 'manage'}
          initialCategory={editingCategory ?? undefined}
          onSaved={() => refetch()}
        />

        {/* ── Confirm delete dialog (bottom sheet) ── */}
        <Dialog
          open={!!confirmDeleteServicio}
          onOpenChange={(o) => !o && setConfirmDeleteServicio(null)}
        >
          <DialogContent
            className="
              z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
              [&>button:last-child]:hidden
              flex flex-col
              !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
              w-full max-w-none rounded-t-2xl rounded-b-none
              data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
              sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
              sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
            "
          >
            <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
                  Eliminar servicio
                </DialogTitle>
                <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
                  Esta acción no se puede deshacer.
                </DialogDescription>
              </DialogHeader>
              <button
                onClick={() => setConfirmDeleteServicio(null)}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 sm:px-6 pb-5">
              <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  ¿Estás seguro de que quieres eliminar el servicio{' '}
                  <strong className="text-gray-900 dark:text-white">
                    "{confirmDeleteServicio?.servicio}"
                  </strong>
                  ? Se eliminarán también sus fases, horarios, días y
                  asignaciones de trabajadores.
                </p>
              </div>
            </div>

            <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
              <Button
                variant="outline"
                onClick={() => setConfirmDeleteServicio(null)}
                className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleEliminarServicio}
                className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
              >
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Contenido principal ── */}
        <AnimatePresence>
          {loading ? (
            <motion.div
              variants={itemVariants}
              className="flex justify-center items-center h-32"
            >
              <motion.div
                variants={rowVariants}
                className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
              />
            </motion.div>
          ) : servicios.length === 0 ? (
            <Card className={`rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="py-16 text-center">
                <Wrench className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  {t('services.noServices')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('services.noServicesText')}
                </p>
                <Button
                  onClick={openCreateModal}
                  className="bg-[#103254] hover:bg-[#0d2742] rounded-lg text-white font-medium shadow-md px-6 py-2 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" /> {t('services.addBtn')}
                </Button>
              </CardContent>
            </Card>
          ) : filtered.length === 0 ? (
            <Card className={`rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="py-16 text-center">
                <SearchX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  {t('services.noResults')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('services.noResultsText')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Vista móvil */}
              <div className="block lg:hidden space-y-6">
                {grupos.map((g) => {
                  const key = g.categoriaId ?? '__sin_categoria__';
                  const collapsed = collapsedGroups.has(key);
                  const label = g.categoria || 'Sin categoría';
                  return (
                    <div key={key} className="space-y-3 tour-category-header">
                      <div className="flex items-center gap-2">
                        {g.categoriaImagen && (
                          <img
                            src={g.categoriaImagen}
                            alt={label}
                            className="w-6 h-6 rounded-full object-cover border border-gray-300 dark:border-white/10"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => toggleGroup(key)}
                          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#103254] dark:text-blue-300 hover:opacity-80 transition-opacity"
                        >
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${
                              collapsed ? '-rotate-90' : ''
                            }`}
                          />
                          {label}
                          <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal normal-case tracking-normal">
                            ({g.servicios.length})
                          </span>
                        </button>
                        {g.categoriaId && (
                          <button
                            type="button"
                            onClick={() => handleEditCategory(g)}
                            className="text-gray-400 hover:text-[#103254] dark:hover:text-blue-400 transition-colors"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {!collapsed &&
                        g.servicios
                          .filter((s) =>
                            visiblesPaginados.some(
                              (v) => v.id_servicio === s.id_servicio
                            )
                          )
                          .map((s) => (
                            <motion.div
                              key={s.id_servicio}
                              variants={rowVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                            >
                              <Card className={`rounded-xl ${GLASS_CARD}`}>
                                <CardContent className="p-4 space-y-3">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <h3 className="font-semibold text-gray-900 dark:text-white text-base flex items-center gap-2">
                                        {s.icono && (
                                          <LucideIcon
                                            name={s.icono}
                                            className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0"
                                          />
                                        )}
                                        {s.servicio}
                                      </h3>
                                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {s.descripcion || '-'}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-1 ml-2">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEditModal(s)}
                                        className="text-[#103254] hover:text-blue-800 dark:hover:text-blue-400 rounded-full tour-edit-btn hover:bg-gray-100 dark:hover:bg-white/10"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled={s.tiene_citas}
                                        onClick={() =>
                                          setConfirmDeleteServicio(s)
                                        }
                                        title={
                                          s.tiene_citas
                                            ? 'No se puede eliminar: tiene citas asociadas'
                                            : 'Eliminar servicio'
                                        }
                                        className="tour-delete-btn rounded-full text-red-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/20"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400 text-xs">
                                        {t('services.cardPrice')}
                                      </span>
                                      <p className="font-medium text-gray-800 dark:text-white">
                                        {s.precio != null
                                          ? `${s.precio.toFixed(2)} €`
                                          : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400 text-xs">
                                        {t('services.cardDuration')}
                                      </span>
                                      <p className="font-medium text-gray-600 dark:text-gray-300">
                                        {formatDuration(
                                          s.duracion_minutos ?? undefined
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedService(s);
                                      setDetailsOpen(true);
                                    }}
                                    className="tour-details-btn w-full text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 hover:bg-[#103254] hover:text-white dark:hover:bg-blue-600"
                                  >
                                    {t('services.viewDetails')}{' '}
                                    <ChevronDown className="w-4 h-4 ml-2" />
                                  </Button>
                                </CardContent>
                              </Card>
                            </motion.div>
                          ))}
                    </div>
                  );
                })}
              </div>

              {/* Vista desktop */}
              <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
                <CardContent className="overflow-x-auto p-0">
                  <table className="min-w-full table-auto text-sm tour-table">
                    <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 w-8" />
                        <th
                          onClick={() => handleSort('servicio')}
                          className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colService')}{' '}
                          {renderSortIcon('servicio')}
                        </th>
                        <th
                          onClick={() => handleSort('descripcion')}
                          className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colDescription')}{' '}
                          {renderSortIcon('descripcion')}
                        </th>
                        <th
                          onClick={() => handleSort('precio')}
                          className="px-4 py-3 text-left cursor-pointer w-28 hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colPrice')} {renderSortIcon('precio')}
                        </th>
                        <th
                          onClick={() => handleSort('duracion')}
                          className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colDuration')}{' '}
                          {renderSortIcon('duracion')}
                        </th>
                        <th
                          onClick={() => handleSort('publico')}
                          className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colPublic')} {renderSortIcon('publico')}
                        </th>
                        <th
                          onClick={() => handleSort('fases')}
                          className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colPhases')} {renderSortIcon('fases')}
                        </th>
                        <th
                          onClick={() => handleSort('activo')}
                          className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                        >
                          {t('services.colActive')} {renderSortIcon('activo')}
                        </th>
                        <th className="px-4 py-3 text-left">
                          {t('services.colMore')}
                        </th>
                        <th className="px-4 py-3 text-left">
                          {t('services.colActions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                      {grupos.map((g) => {
                        const key = g.categoriaId ?? '__sin_categoria__';
                        const collapsed = collapsedGroups.has(key);
                        const isOver = overGroupKey === key;
                        return (
                          <Fragment key={key}>
                            <DroppableGroupHeader
                              grupo={g}
                              collapsed={collapsed}
                              onToggle={() => toggleGroup(key)}
                              isOver={isOver}
                              onEditCategory={handleEditCategory}
                            />
                            {!collapsed &&
                              g.servicios
                                .filter((s) =>
                                  visiblesPaginados.some(
                                    (v) => v.id_servicio === s.id_servicio
                                  )
                                )
                                .map((s, idx) => {
                                  const isDraggingThis =
                                    activeServicio?.id_servicio ===
                                    s.id_servicio;
                                  return (
                                    <DraggableRow
                                      key={s.id_servicio}
                                      servicio={s}
                                      idx={idx}
                                      isDragging={isDraggingThis}
                                    >
                                      {({ dragListeners, dragAttributes }) => (
                                        <>
                                          <td className="px-3 py-3 w-8">
                                            <button
                                              {...dragListeners}
                                              {...dragAttributes}
                                              className="cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-600 hover:text-[#103254] dark:hover:text-blue-400 transition-colors touch-none tour-drag-handle"
                                              title="Arrastrar para cambiar categoría"
                                              tabIndex={-1}
                                            >
                                              <GripVertical className="w-4 h-4" />
                                            </button>
                                          </td>
                                          <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">
                                            <span className="flex items-center gap-2">
                                              {s.icono && (
                                                <LucideIcon
                                                  name={s.icono}
                                                  className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0"
                                                />
                                              )}
                                              {s.servicio}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                            {s.descripcion || '-'}
                                          </td>
                                          <td className="px-4 py-3 text-gray-800 dark:text-white">
                                            {s.precio != null
                                              ? `${s.precio.toFixed(2)} €`
                                              : '-'}
                                          </td>
                                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                            {formatDuration(
                                              s.duracion_minutos ?? undefined
                                            )}
                                          </td>
                                          <td className="px-4 py-3">
                                            <span
                                              className={`px-2 py-1 rounded-full text-xs font-semibold tour-publico-switch ${
                                                s.servicio_publico
                                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                              }`}
                                            >
                                              {s.servicio_publico
                                                ? t('services.yes')
                                                : t('services.no')}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5">
                                              <span
                                                className={`px-2 py-1 rounded-full text-xs font-semibold tour-fases-badge ${
                                                  (s.fases_servicio?.length ?? 0) > 0
                                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                                }`}
                                              >
                                                {(s.fases_servicio?.length ?? 0) > 0 ? t('services.yes') : t('services.no')}
                                              </span>
                                              {desvioFases(s) > 0 && (
                                                <span
                                                  className="inline-flex text-amber-500"
                                                  title={`Las fases suman ${desvioFases(s)} min mas que la duracion del servicio`}
                                                >
                                                  <AlertTriangle className="w-4 h-4" />
                                                </span>
                                              )}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span
                                              className={`px-2 py-1 rounded-full text-xs font-semibold tour-activo-badge ${
                                                s.activo
                                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                              }`}
                                            >
                                              {s.activo ? t('services.yes') : t('services.no')}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => {
                                                setSelectedService(s);
                                                setDetailsOpen(true);
                                              }}
                                              className="tour-details-btn text-[#103254] hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
                                            >
                                              <ChevronDown className="w-5 h-5" />
                                            </Button>
                                          </td>
                                          <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEditModal(s)}
                                                className="text-[#103254] hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 rounded-full tour-edit-btn hover:bg-gray-100 dark:hover:bg-white/10"
                                              >
                                                <Pencil className="w-5 h-5" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                disabled={s.tiene_citas}
                                                onClick={() =>
                                                  setConfirmDeleteServicio(s)
                                                }
                                                title={
                                                  s.tiene_citas
                                                    ? 'No se puede eliminar: tiene citas asociadas'
                                                    : 'Eliminar servicio'
                                                }
                                                className="tour-delete-btn rounded-full text-red-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/20"
                                              >
                                                <Trash2 className="w-5 h-5" />
                                              </Button>
                                            </div>
                                          </td>
                                        </>
                                      )}
                                    </DraggableRow>
                                  );
                                })}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={visibles.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={changePageSize}
                itemLabel="servicios"
                className="tour-pagination"
              />
            </>
          )}
        </AnimatePresence>
      </motion.div>

      <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }}>
        {activeServicio ? <DragOverlayCard servicio={activeServicio} /> : null}
      </DragOverlay>
    </DndContext>
  );
}